import { ICellRendererParams } from "ag-grid-community";
import { useState } from "react";

import { AddWebhookAction } from "./AddWebhookAction";
import { SecretKeyDisplay } from "./SecretKeyDisplay";
import { WebhookData } from "./types";
import { WebhookSecretModal } from "./WebhookSecretModal";

import { DataTable } from "@/components/ui/ag-grid-table";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { TableColumn } from "@/components/ui/TableV2";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import { useDeleteWebhook } from "@/queries";

export const Webhooks = () => {
  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const userContext = useUser();
  const [openWebhookSecretModal, setOpenWebhookSecretModal] = useState(false);
  const [secretSignature, setSecretSignature] = useState("");
  const auth = useAuth();
  const { mutate: deleteWorkspaceWebhook } = useDeleteWebhook(auth?.userToken ?? "");

  const webhooksData: WebhookData[] = userContext.workspaceWebhooks.map(webhook => ({
    id: webhook.id,
    url: webhook.url,
    secret_key: webhook.webhook?.secret_key || webhook.secret_key,
  }));

  const columns: TableColumn<WebhookData>[] = [
    {
      id: 0,
      name: "Webhook URL",
      header: "Webhook URL",
      field: "url",
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params: ICellRendererParams<WebhookData>) => (
        <OverflowDisplayTooltip content={params.data?.url}>
          <span className="text-sm text-gray-600">{params.data?.url}</span>
        </OverflowDisplayTooltip>
      ),
    },
    {
      id: 1,
      name: "Secret Key",
      header: "Secret Key",
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params: ICellRendererParams<WebhookData>) => (
        <SecretKeyDisplay secretKey={params.data?.secret_key || ""} />
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <DataTable<WebhookData>
        data={webhooksData}
        columns={columns}
        action={
          <AddWebhookAction
            onWebhookCreated={(_, secretKey) => {
              setOpenWebhookSecretModal(true);
              setSecretSignature(secretKey);
            }}
          />
        }
        emptyStateMessage="No webhooks found"
        fullHeight={false}
        height="400px"
        rowsDeletable
        onRequestDeleteConfirm={onRequestDeleteConfirm}
        onDelete={webhook => {
          if (webhook) {
            deleteWorkspaceWebhook(webhook.id);
          }
        }}
        objectName="webhook"
      />
      <WebhookSecretModal
        open={openWebhookSecretModal}
        setOpen={setOpenWebhookSecretModal}
        secretSignature={secretSignature}
      />
    </div>
  );
};
